import React, { useEffect, useMemo, useState } from "react";
import { AccountHolderRatePlan } from "../../../services/accountService";
import { RatePlansContext } from "./ratePlansContext";
import {
  RatePlan,
  RatePlanType,
} from "../../../pages/customerSettings/RatePlans";

type RatePlansProviderProps = {
  initialRatePlans: AccountHolderRatePlan[];
  initialRatePlanType: RatePlanType;
  country: string;
  children: React.ReactNode;
};

export function RatePlansProvider({
  initialRatePlans,
  initialRatePlanType,
  country,
  children,
}: RatePlansProviderProps) {
  /* AccountHolderRatePlans for each account holder coming from the server */
  const [accountHolderRatePlans, setAccountHolderRatePlans] =
    useState<AccountHolderRatePlan[]>(initialRatePlans);
  const [ratePlanType, setRatePlanType] =
    useState<RatePlanType>(initialRatePlanType);

  useEffect(() => {
    setAccountHolderRatePlans(initialRatePlans);
  }, [initialRatePlans]);

  //#region Provider functions
  /* Function to modify the context */
  const modify = (
    ratePlan: RatePlan,
    changes: Partial<AccountHolderRatePlan>
  ) => {
    setAccountHolderRatePlans((prev) =>
      prev.map((rp) =>
        rp.paymentMethodType === ratePlan.id ? { ...rp, ...changes } : rp
      )
    );
  };

  /* Function to update the rate plan type */
  const updateRatePlanType = (newType: RatePlanType) => {
    setRatePlanType(newType);
  };

  /* Resets the Rate Plans back to their initial state before modification */
  const reset = () => {
    setAccountHolderRatePlans(initialRatePlans);
  };
  //#endregion

  //#region Provider values
  // Build the lookup only once per `initialRatePlans` change
  const initialLookup = useMemo(
    () =>
      new Map(initialRatePlans.map((p) => [p.paymentMethodType, p] as const)),
    [initialRatePlans]
  );

  /* Indicates if the Save button should be enabled based on required fields */
  const saveButtonEnabled = useMemo(() => {
    const requiredFields: (keyof AccountHolderRatePlan)[] =
      ratePlanType === "Flat Rate Plan"
        ? ["fixedAmount", "percentageValue", "ippPercentage", "ippFixedAmount"]
        : [
            "icFixedAmount",
            "icPercentage",
            "icIppPercentage",
            "icIppFixedAmount",
          ];

    // All plans must have required numeric fields > 0
    const allValid = accountHolderRatePlans.every((plan) =>
      requiredFields.every((f) => Number(plan[f] as any) > 0)
    );
    if (!allValid) return false;

    // At least one plan has any modification
    const anyPlanModified = accountHolderRatePlans.some((plan) => {
      const initial = initialLookup.get(plan.paymentMethodType);
      if (!initial) return false;

      const numericModified = requiredFields.some(
        (f) => Number(plan[f] as any) !== Number(initial[f] as any)
      );

      const metaModified =
        plan.allowedMethod !== initial.allowedMethod ||
        plan.surcharge !== initial.surcharge;

      return numericModified || metaModified;
    });

    return anyPlanModified;
  }, [accountHolderRatePlans, initialLookup, ratePlanType]);

  /* Manually specified Rate Plans including country-specific configurations */
  const definedRatePlans = useMemo((): RatePlan[] => {
    let ratePlans: RatePlan[] = [
      { id: "visa", label: "Visa" },
      { id: "mc", label: "Mastercard" },
      { id: "amex", label: "AMEX" },
      { id: "discover", label: "Discover" },
    ];
    // Add ACH to rate plans if country is not AU
    if (country !== "AU") {
      if (!ratePlans.some((plan) => plan.id === "ach")) {
        ratePlans.push({ id: "ach", label: "ACH" });
      }
    }
    // Add JCB and EFTPOS to rate plans if country is AU and they are not already in the list
    if (country === "AU") {
      if (!ratePlans.some((plan) => plan.id === "jcb")) {
        ratePlans.push({ id: "jcb", label: "JCB" });
      }
      if (!ratePlans.some((plan) => plan.id === "eftpos_australia")) {
        ratePlans.push({ id: "eftpos_australia", label: "EFTPOS" });
      }
      if (!ratePlans.some((plan) => plan.id === "cup")) {
        ratePlans.push({ id: "cup", label: "CUP" });
      }
    }
    // Add Interac to rate plans if country is CA and it is not already in the list
    if (country === "CA") {
      if (!ratePlans.some((plan) => plan.id === "interac_card")) {
        ratePlans.push({ id: "interac_card", label: "Interac" });
      }
      if (!ratePlans.some((plan) => plan.id === "eft_directdebit_CA")) {
        ratePlans.push({ id: "eft_directdebit_CA", label: "EFTPAD" });
      }
    }
    return ratePlans;
  }, [country]);

  /* Rate Plans specific to each account holder and country of origin merged with definedRatePlans */
  const definedAccountHolderRatePlans = useMemo(
    () =>
      definedRatePlans.map((definedPlan) => {
        const matchingAccountHolderPlan = accountHolderRatePlans.find(
          (accountHolderPlan) =>
            accountHolderPlan.paymentMethodType === definedPlan.id
        );
        return {
          ...definedPlan,
          data: matchingAccountHolderPlan,
        };
      }),
    [definedRatePlans, accountHolderRatePlans]
  );

  /* Modified AccountHolderRatePlans that differ from the initial state */
  const modifiedRatePlans = useMemo(
    () =>
      accountHolderRatePlans.filter((plan) => {
        const initial = initialLookup.get(plan.paymentMethodType);
        if (!initial) return false;
        return JSON.stringify(plan) !== JSON.stringify(initial);
      }),
    [accountHolderRatePlans, initialLookup]
  );
  //#endregion

  return (
    <RatePlansContext.Provider
      value={{
        definedRatePlans,
        definedAccountHolderRatePlans,
        accountHolderRatePlans,
        modifiedRatePlans,
        modify,
        reset,
        saveButtonEnabled,
        updateRatePlanType,
      }}
    >
      {children}
    </RatePlansContext.Provider>
  );
}
